import type { Coord } from '../data/communications'

/** A document joined to the notice, shown as a download in the email. */
export interface Attachment {
  id: string
  name: string
  /** Size in bytes. */
  size: number
}

export interface EmailChannel {
  active: boolean
  subject: string
  /** Rich-text HTML authored in the editor — images are embedded inline. */
  body: string
  attachments: Attachment[]
}

export interface SmsChannel {
  active: boolean
  text: string
}

export interface VoiceChannel {
  active: boolean
  script: string
  voice: string
}

export interface PushChannel {
  active: boolean
  title: string
  message: string
}

export interface WebsiteChannel {
  active: boolean
  bannerText: string
  ctaLabel: string
}

export interface SocialChannel {
  active: boolean
  /** Rich-text HTML authored in the editor — images are embedded inline. */
  postText: string
}

/** Digital signage in municipal buildings (arena, library, city hall…). */
export interface ScreensChannel {
  active: boolean
  headline: string
  bodyText: string
  /** Seconds each slide stays on screen. */
  duration: string
  /** Ids of the screen locations the slide is pushed to. */
  locations: string[]
}

export interface ChannelsState {
  email: EmailChannel
  sms: SmsChannel
  voice: VoiceChannel
  push: PushChannel
  website: WebsiteChannel
  social: SocialChannel
  screens: ScreensChannel
}

/**
 * One place in the "Lieu" part of the audience. Places are combined as a
 * union: a district + three houses reaches everyone in either.
 */
export type AudiencePlace =
  | { id: string; kind: 'sector'; sectorId: string }
  | { id: string; kind: 'address'; label: string; coord: Coord }
  | { id: string; kind: 'radius'; label: string; coord: Coord; radius: string }
  | { id: string; kind: 'area'; points: Coord[] }

export type AudiencePlaceKind = AudiencePlace['kind']

/**
 * Audience = WHERE (whole city, or a union of places) intersected with WHO
 * (department subscribers, age brackets, residence). Every "who" filter left
 * empty means "no restriction", so filters always combine.
 */
export interface AudienceState {
  scope: 'all' | 'places'
  places: AudiencePlace[]
  selectedDepartments: string[]
  selectedAges: string[]
  residence: string
}

/** What happens when the composer is submitted. */
export type SendMode = 'now' | 'scheduled' | 'template'

export interface ScheduleState {
  mode: SendMode
  /** ISO date (yyyy-mm-dd) used when mode is "scheduled". */
  date: string
  /** 24h time (hh:mm) used when mode is "scheduled". */
  time: string
  /** Template metadata used when mode is "template". */
  templateName: string
  templateDescription: string
}

export const EMPTY_CHANNELS: ChannelsState = {
  email: { active: false, subject: '', body: '', attachments: [] },
  sms: { active: false, text: '' },
  voice: { active: false, script: '', voice: 'femme' },
  push: { active: false, title: '', message: '' },
  website: { active: false, bannerText: '', ctaLabel: '' },
  social: { active: false, postText: '' },
  screens: {
    active: false,
    headline: '',
    bodyText: '',
    duration: '12',
    locations: ['scr-hotel', 'scr-arena', 'scr-biblio'],
  },
}

export const EMPTY_AUDIENCE: AudienceState = {
  scope: 'all',
  places: [],
  selectedDepartments: [],
  selectedAges: [],
  residence: 'all',
}

export const EMPTY_SCHEDULE: ScheduleState = {
  mode: 'now',
  date: '',
  time: '09:00',
  templateName: '',
  templateDescription: '',
}
